import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  StackDivider,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import AppIdentity from "components/AppIdentity";
import { useMemberAuthStore } from "modules/members/auth";
import { formatAsDate } from "modules/utils";
import { appDisplayName } from "modules/apps/tenants";
import * as R from "ramda";
import { Suspense } from "react";
import {
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiUser,
} from "react-icons/fi";
import { MFApp } from "state/members/apps";
import { MFOffboardingAccount } from "state/members/offboarding";
import Row from "./Row";

const AppGroupView = ({
  accounts,
  app,
  onSelect,
  isSelected,
}: {
  accounts: MFOffboardingAccount[];
  onSelect: (id: string | undefined) => any;
  isSelected: boolean;
  app: MFApp;
}) => {
  const memberId = useMemberAuthStore((state) => state.id);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isSelected });
  const status = accounts.every((a) =>
    ["complete", "declined"].includes(a.status),
  )
    ? "Complete"
    : "In progress";
  const completed = accounts.filter((a) =>
    ["complete", "declined"].includes(a.status),
  ).length;

  const sorted = R.sortBy(
    (a: MFOffboardingAccount) => a.memberAccount?.member?.name || "a",
    accounts,
  );

  if (!app) {
    return null;
  }

  const removalTasks = sorted.filter(
    (a) => a.assigneeId === memberId && a.offboardingStatus === "started",
  );
  const removalTasksRemaining = removalTasks.filter((a) => !a.accountRemovedAt && a.status === "incomplete");
  const adminTasks = sorted.filter((a) => a.adminRightsAssigneeId === memberId);
  const adminTasksRemaining = adminTasks.filter(
    (a) => !a.adminRightsConfirmedAt,
  );

  return (
    <>
      <VStack w="full" align="flex-start">
        {isOpen && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="gray"
            leftIcon={<Icon as={FiArrowLeft} />}
            onClick={() => {
              onSelect(undefined);
              onToggle();
            }}
          >
            Back to all offboardings
          </Button>
        )}
        <HStack
          w="full"
          justify={"space-between"}
          opacity={!isOpen && completed ? 0.5 : 1}
          p={1}
          px={2}
          onClick={() => {
            onSelect(app.id);
            onToggle();
          }}
          border="1px solid transparent"
          rounded={8}
          _hover={{
            bg: "gray.50",
            border: "1px solid",
            borderColor: "gray.200",
            cursor: "pointer",
          }}
        >
          <AppIdentity app={app} />
          <HStack spacing={4}>
            {accounts[0]?.dueDate && (
              <Tooltip label="Due Date">
                <HStack spacing={1}>
                  <Icon as={FiClock} color="gray.600" />
                  <Text fontSize="sm" fontWeight={400} color="gray.600">
                    {formatAsDate(new Date(accounts[0]?.dueDate))}
                  </Text>
                </HStack>
              </Tooltip>
            )}
            {completed !== accounts.length ? (
              <HStack spacing={1}>
                <Icon color="gray.600" as={FiUser} />
                <Text fontSize="sm" fontWeight={400} color="gray.600">
                  {completed}/{accounts.length}
                </Text>
              </HStack>
            ) : (
              <Icon as={FiCheckCircle} color="green.700" boxSize="18px" />
            )}
            <Tag
              size="sm"
              colorScheme={status === "Complete" ? "green" : "orange"}
            >
              <TagLabel textTransform={"capitalize"}>{status}</TagLabel>
            </Tag>
          </HStack>
        </HStack>
      </VStack>
      <Collapse in={isOpen} style={{ width: "100%" }}>
        <Box px={2}>
          <Suspense fallback={<Spinner />}>
            <Box py={4}>
              <Divider mb={4} />
              <Tabs
                variant="enclosed"
                w="full"
                defaultIndex={adminTasksRemaining.length === 0 ? 1 : 0}
                isLazy
              >
                <TabList>
                  <Tab>
                    Admin transfer tasks
                    <Flex
                      ml={3}
                      justify="center"
                      align="center"
                      w="20px"
                      h="20px"
                      bg={
                        adminTasksRemaining.length < 1
                          ? "green.100"
                          : "orange.100"
                      }
                      borderRadius={"100%"}
                    >
                      {adminTasksRemaining.length < 1 ? (
                        <Icon as={FiCheck} color="green.700" boxSize="10px" />
                      ) : (
                        <Text fontSize="xs" color="orange.700">
                          {adminTasksRemaining.length}
                        </Text>
                      )}
                    </Flex>
                  </Tab>
                  <Tab>
                    Account Removal tasks
                    <Flex
                      ml={3}
                      justify="center"
                      align="center"
                      w="20px"
                      h="20px"
                      bg={
                        removalTasksRemaining.length < 1
                          ? "green.100"
                          : "red.100"
                      }
                      borderRadius={"100%"}
                    >
                      {removalTasksRemaining.length < 1 ? (
                        <Icon as={FiCheck} color="green.700" boxSize="10px" />
                      ) : (
                        <Text fontSize="xs" color="red.700">
                          {removalTasksRemaining.length}
                        </Text>
                      )}
                    </Flex>
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px={0} pt={6}>
                    {adminTasks.length ? (
                      <>
                        <Text fontSize={"sm"} color="gray.600">
                          Below is a list of accounts where you need to transfer
                          or remove admin rights/role.
                        </Text>
                        <HStack w="full" mt={6}>
                          <Box w="75%">
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              fontWeight="500"
                            >
                              App
                            </Text>
                          </Box>
                          <Flex w="25%">
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              fontWeight="500"
                            >
                              Action
                            </Text>
                          </Flex>
                        </HStack>
                        <Divider mt={3} mb={6} />
                        <VStack
                          w="full"
                          align="flex-start"
                          divider={<StackDivider />}
                          spacing={4}
                        >
                          {adminTasks.map((a) => (
                            <Row key={a.id} account={a} type={"admin"} />
                          ))}
                        </VStack>
                      </>
                    ) : (
                      <Text fontSize={"sm"} color="gray.600">
                        No tasks found.
                      </Text>
                    )}
                  </TabPanel>
                  <TabPanel px={0} pt={6}>
                    {removalTasks.length ? (
                      <>
                        <Text fontSize={"sm"} color="gray.600">
                          Below is a list of accounts you need to remove from{" "}
                          {appDisplayName(app)}.
                        </Text>
                        <HStack w="full" mt={6}>
                          <Box w="65%">
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              fontWeight="500"
                            >
                              App
                            </Text>
                          </Box>
                          <Flex w="35%" justify={"center"}>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              fontWeight="500"
                            >
                              Action
                            </Text>
                          </Flex>
                        </HStack>
                        <Divider mt={3} mb={6} />
                        <VStack
                          w="full"
                          align="flex-start"
                          divider={<StackDivider />}
                          spacing={4}
                        >
                          {removalTasks.map((a) => (
                            <Row
                              key={a.id}
                              account={a}
                              type={"account"}
                              showApp={false}
                            />
                          ))}
                        </VStack>
                      </>
                    ) : (
                      <Text fontSize={"sm"} color="gray.600">
                        No tasks found.
                      </Text>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Suspense>
        </Box>
      </Collapse>
    </>
  );
};

export default AppGroupView;
